/**
 * 대시보드 위젯의 상대 시각 — 「방금 · N분 전 · N시간 전 · M/D」.
 *
 * ## ⚠ 왜 한 곳인가 — 같은 판의 위젯들이 «다른 글자»로 말하고 있었다
 *
 * 2026-09-16 에 셋이 따로 있었고 하루가 넘으면 서로 달랐다:
 * ```
 *   알림 · 공지사항 (InfoWidgets)        9/15
 *   최근 문서       (WorkDetailWidgets)  09-15     ← 같은 판, 다른 글자
 *   최근 대화       (여기 · 새로 만든 집)  9/15
 * ```
 * 2026-09-22 에 **셋을 여기로 모았다.** 글자는 **알림 쪽(`9/15`)** 으로 통일했다 — 셋 중 둘이
 * 이미 그 말이었고, 시안도 「알림 위젯이 쓰는 것과 같은 글자」라고 적었다.
 *
 * ## ★★★ 「지금」을 «인자로 받는다» — 시한폭탄을 원천에서 막는다
 *
 * 벽시계를 함수가 스스로 읽으면, 그것을 쓰는 화면의 시험은 **시계를 박지 않는 한 하루 중
 * 어느 때만 깨진다.** 2026-09-16~22 에 그 부류로 넷을 겪었다(회의실 현황 · 휴일대체 ·
 * 연차촉진 · 관리콘솔 음성 저장). **「지금」을 밖에서 받으면 그 일이 아예 안 생긴다** —
 * 이미 `components/shared/CommentThread` 의 `formatCommentTime(iso, now)` 가 그 모양이다.
 *
 * ★ 화면은 `now` 를 안 줘도 된다(기본값이 「지금」이다). **시험이 줄 수 있으면 충분하다.**
 *
 * ## ★★ 못 읽는 값은 «빈 글자»다
 *
 * `'—'` 같은 자리표시를 지어내지 않는다 — 시각이 없는 줄은 그 칸이 통째로 비는 편이 낫다.
 *
 * ## ⛔ 여기로 «안» 모은 것 — 결재함
 *
 * `pages/ApprovalPage/box/ApprovalBoxPage.tsx` 에도 `ago` 가 있는데 **말이 다르다**
 * (「방금」이 없고 「어제」가 있고, 하루가 넘어도 날짜로 안 넘어가고 「N일 전」으로 계속 간다).
 * 합치려면 **결재함의 화면 글자가 바뀌므로 별개 결정**이다 — 그래서 손대지 않았다.
 */
export function agoText(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';

  const min = Math.floor((now.getTime() - t) / 60_000);
  // 앞날의 시각(시계가 어긋난 서버 · 잘못 들어간 값)은 「방금」으로 본다 — 「-3분 전」을 적지 않는다.
  if (min < 1) return '방금';
  if (min < 60) return `${min}분 전`;
  if (min < 60 * 24) return `${Math.floor(min / 60)}시간 전`;

  const d = new Date(t);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}
